import propz from 'propz';
import { ProgrammeFieldDef } from 'Src/helpers/service/admin/clubProgrammes';

export type FieldType = 'STRING' | 'NUMBER' | 'ENUM' | 'BOOLEAN';

export type ProgrammeField = ProgrammeFieldDef & {
  _id?: any;
};

export type UiField = ProgrammeField & { id: string };

export type TemplateUiRow = {
  id: string;
  idx: number;
  isRequired: boolean;
};

export const fieldTypeLabel = (t: any) => String(t || 'STRING').toUpperCase();

export const normalizeToUiFields = (fields: ProgrammeField[]): UiField[] => {
  const arr = Array.isArray(fields) ? fields : [];

  return arr.map((f, idx) => {
    const serverId = String((f as any)._id || (f as any).id || '');
    const id = serverId || `local_${idx}`;

    const type = String((f as any).type || '').toUpperCase();
    const isEnum = type === 'ENUM';

    let enumOptions: string[] | undefined = undefined;
    if (isEnum) {
      const raw = Array.isArray((f as any).enumOptions) ? (f as any).enumOptions : [];
      const cleaned = raw.map(x => String(x || '').trim()).filter(Boolean);
      enumOptions = Array.from(new Set(cleaned));
    }

    return {
      ...f,
      ...(isEnum ? { enumOptions } : {}),
      id
    };
  });
};

export const stripUi = (fields: UiField[]): ProgrammeField[] => {
  return (fields || []).map(f => {
    const { id, ...rest } = f as any;
    return rest as ProgrammeField;
  });
};

export const stableStringify = (v: any) => JSON.stringify(v ?? null);

export const toDirtyComparable = (fields: UiField[]) => {
  const plain = stripUi(fields);

  return (plain || []).map((f: any) => {
    const type = String(f?.type || 'STRING').toUpperCase();

    return {
      heading: String(f?.heading || ''),
      type,
      isRequired: !!f?.isRequired,
      isDefault: !!f?.isDefault,
      ...(type === 'ENUM'
        ? {
            enumOptions: Array.from(
              new Set(
                (Array.isArray(f?.enumOptions) ? f.enumOptions : [])
                  .map((x: any) => String(x || '').trim())
                  .filter(Boolean)
              )
            )
          }
        : {})
    };
  });
};

export const getTemplateFieldsAny = (tpl: any): any[] => {
  const t = tpl || {};
  const a =
    propz.get(t, ['fields'], null) ||
    propz.get(t, ['defaultFields'], null) ||
    propz.get(t, ['consentRequest', 'fields'], null) ||
    propz.get(t, ['confirmationRequest', 'fields'], null) ||
    [];

  return Array.isArray(a) ? a : [];
};

export const getTemplateHeading = (f: any, idx: number) =>
  String(f?.heading || f?.title || f?.label || f?.question || `Template field ${idx + 1}`);

export const getTemplateType = (f: any): FieldType => {
  const t = String(f?.type || 'STRING').toUpperCase();
  const allowed: FieldType[] = ['STRING', 'NUMBER', 'ENUM', 'BOOLEAN'];
  return (allowed.includes(t as any) ? (t as FieldType) : 'STRING') as FieldType;
};

export const getTemplateRequired = (f: any) => !!(f?.required || f?.isRequired);

export const getTemplateEnumValues = (f: any): string[] => {
  const v = f?.enumValues || f?.enumOptions || f?.options || f?.values || f?.enum || [];
  const arr = Array.isArray(v) ? v : [];
  return arr.map((x: any) => String(x).trim()).filter(Boolean);
};

export const normalizeKey = (s: any) =>
  String(s || '')
    .trim()
    .toLowerCase();

